import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PlacementAnalyticsService, {
    PlacementInstituteRow,
    PlacementListCriteria,
} from './PlacementAnalyticsService';
import { instituteShellPath } from './deepLinks';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import { DomainElement } from '../../../library/Core/SmartTypes';
import './placementAnalytics.css';

const useDebounced = <T,>(value: T, delay = 350): T => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
};

const TEMPLATE_LABEL: Record<string, string> = {
    B_SCHOOL: 'B-School',
    ENGINEERING: 'Engineering',
    LAW_SCHOOL: 'Law College',
};

const TEMPLATE_CLASS: Record<string, string> = {
    B_SCHOOL: 'pa-badge-cyan',
    ENGINEERING: 'pa-badge-blue',
    LAW_SCHOOL: 'pa-badge-orange',
};

const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const clientLabel = (clientType?: string | null) => {
    if (!clientType) return '—';
    const t = String(clientType).toUpperCase();
    if (t === 'CLIENT') return 'Client';
    if (t === 'NON_CLIENT' || t === 'NON-CLIENT' || t === 'PROSPECT') return 'Non-Client';
    return clientType;
};

const PlacementAnalyticsListing = () => {
    const navigate = useNavigate();
    const { globalState } = useContext(GlobalContext);
    const [criteria, setCriteria] = useState<PlacementListCriteria>(PlacementAnalyticsService.emptyListCriteria());
    const [rows, setRows] = useState<PlacementInstituteRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openMenuId, setOpenMenuId] = useState<number | null>(null);
    const [moduleMap, setModuleMap] = useState<Record<number, boolean>>({});
    const [showFilters, setShowFilters] = useState(true);

    const domain = (globalState as any)?.domain as Map<string, DomainElement[]> | undefined;

    const cityName = useCallback(
        (code?: string | null) => {
            if (!code || !domain) return code || '—';
            const hit = domain.get('CITY_CODE')?.find((e) => String(e.code) === String(code));
            return hit?.value || String(code);
        },
        [domain]
    );

    const jpGroups = useMemo(() => {
        if (!domain) return [];
        const institutes = domain.get('INSTITUTE_DOMAIN') || [];
        const jpGroupIds = new Set<string>();
        for (const inst of institutes) {
            try {
                const fc = inst.flexCol ? JSON.parse(inst.flexCol as string) : null;
                if (fc?.commitment_paths && Array.isArray(fc.commitment_paths) && fc.commitment_paths.includes('JP')) {
                    if (inst.parentCode) jpGroupIds.add(String(inst.parentCode));
                }
            } catch {
                /* skip */
            }
        }
        const schoolGroups = domain.get('SCHOOL_GROUP') || [];
        return schoolGroups
            .filter((g) => jpGroupIds.has(String(g.code)))
            .map((g) => ({ code: String(g.code), name: String(g.value || g.code) }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [domain]);

    const debounced = useDebounced(criteria);
    const criteriaKey = useMemo(() => JSON.stringify(debounced), [debounced]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [listRes, countRes] = await Promise.all([
                PlacementAnalyticsService.list(debounced),
                PlacementAnalyticsService.listCount(debounced),
            ]);
            const list = (listRes.data || []) as PlacementInstituteRow[];
            setRows(list);
            setTotal(countRes.data?.total ?? list.length);

            const enabled: Record<number, boolean> = {};
            for (const r of list) {
                enabled[r.id] = !!r.moduleEnabled;
            }
            setModuleMap((prev) => ({ ...prev, ...enabled }));
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.message || 'Failed to load institutes');
        } finally {
            setLoading(false);
        }
    }, [debounced]);

    useEffect(() => {
        load();
    }, [criteriaKey, load]);

    const go = (
        instituteId: number,
        tab: 'data' | 'summary' | 'insights' | 'ahli' | 'spm' | 'focus'
    ) => {
        navigate(instituteShellPath('support', instituteId, tab));
        setOpenMenuId(null);
    };

    const toggleModule = async (row: PlacementInstituteRow) => {
        const next = !moduleMap[row.id];
        try {
            await PlacementAnalyticsService.upsertSettings(row.id, { moduleEnabled: next ? 1 : 0 });
            setModuleMap((m) => ({ ...m, [row.id]: next }));
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Failed to update module setting');
        }
        setOpenMenuId(null);
    };

    const setFilter = <K extends keyof PlacementListCriteria>(key: K, value: PlacementListCriteria[K]) => {
        setCriteria((c) => ({ ...c, [key]: value || undefined, offset: 0 }));
    };

    return (
        <div className="main flex-1">
            <div className="mb-4">
                <div className="white-block white-block-notopborderradius p-0 pa-listing-wrap">
                    <div className="p-4 p-md-5 pa-listing">
                        <div className="pa-listing-head">
                            <div>
                                <h2 className="pa-listing-title">Placement Analysis — Institutes</h2>
                                <p className="pa-listing-sub">
                                    Upload pre-analysed Excel files and view placement dashboards across client institutes.
                                </p>
                            </div>
                            <button
                                type="button"
                                className="pa-btn-filters"
                                onClick={() => setShowFilters((v) => !v)}>
                                {showFilters ? 'Hide Filters' : 'Show Filters'}
                            </button>
                        </div>

                        {showFilters && (
                            <div className="pa-filter-card">
                                <div className="pa-filter-grid pa-filter-grid-6">
                                    <label className="pa-filter-field">
                                        <span>Institute Group</span>
                                        <select
                                            className="form-select"
                                            value={String(criteria.instituteGroupId || '')}
                                            onChange={(e) => setFilter('instituteGroupId', e.target.value || undefined)}>
                                            <option value="">All groups</option>
                                            {jpGroups.map((g) => (
                                                <option key={g.code} value={g.code}>
                                                    {g.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="pa-filter-field">
                                        <span>Institute Name</span>
                                        <input
                                            className="form-control"
                                            placeholder="Search institute"
                                            value={criteria.search || ''}
                                            onChange={(e) => setFilter('search', e.target.value)}
                                        />
                                    </label>
                                    <label className="pa-filter-field">
                                        <span>Program Level</span>
                                        <select
                                            className="form-select"
                                            value={criteria.programLevel || ''}
                                            onChange={(e) => setFilter('programLevel', e.target.value || undefined)}>
                                            <option value="">All</option>
                                            <option value="UG">UG</option>
                                            <option value="PG">PG</option>
                                            <option value="DOCTORAL">Doctoral</option>
                                        </select>
                                    </label>
                                    <label className="pa-filter-field">
                                        <span>Data Updated</span>
                                        <select
                                            className="form-select"
                                            value={criteria.dataUpdated || 'ALL'}
                                            onChange={(e) =>
                                                setFilter(
                                                    'dataUpdated',
                                                    e.target.value as PlacementListCriteria['dataUpdated']
                                                )
                                            }>
                                            <option value="ALL">All</option>
                                            <option value="UPLOADED">Uploaded</option>
                                            <option value="NOT_UPLOADED">Not Uploaded</option>
                                        </select>
                                    </label>
                                    <label className="pa-filter-field">
                                        <span>Last Updated From</span>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={criteria.lastUpdatedFrom || ''}
                                            onChange={(e) => setFilter('lastUpdatedFrom', e.target.value || undefined)}
                                        />
                                    </label>
                                    <label className="pa-filter-field">
                                        <span>Last Updated To</span>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={criteria.lastUpdatedTo || ''}
                                            onChange={(e) => setFilter('lastUpdatedTo', e.target.value || undefined)}
                                        />
                                    </label>
                                </div>
                            </div>
                        )}

                        {error && <div className="alert alert-danger py-2 font-13">{error}</div>}

                        <div className="pa-listing-meta">
                            <strong>{total}</strong> institutes · Click institute name to view details &amp; dashboard
                            {loading ? ' · Loading…' : ''}
                        </div>

                        <div className="pa-table-wrap">
                            <table className="table pa-table align-middle">
                                <thead>
                                    <tr>
                                        <th>Institute name</th>
                                        <th>City</th>
                                        <th>Type</th>
                                        <th>Client</th>
                                        <th>Data Status</th>
                                        <th>Analysis</th>
                                        <th>Last Updated</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => {
                                        const typeKey = row.templateType || '';
                                        const typeLabel = TEMPLATE_LABEL[typeKey] || '—';
                                        const dataUploaded = row.dataStatus === 'Uploaded';
                                        const analysis = row.analysisStatus;
                                        return (
                                            <tr key={row.id}>
                                                <td>
                                                    <Link
                                                        to={instituteShellPath('support', row.id, 'data')}
                                                        className="pa-inst-link">
                                                        {row.name}
                                                    </Link>
                                                </td>
                                                <td>{cityName(row.cityCode || row.location)}</td>
                                                <td>
                                                    {typeKey ? (
                                                        <span className={`pa-badge ${TEMPLATE_CLASS[typeKey] || ''}`}>
                                                            {typeLabel}
                                                        </span>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td>
                                                    {row.clientType ? (
                                                        <span
                                                            className={`pa-badge ${
                                                                String(row.clientType).toUpperCase() === 'CLIENT'
                                                                    ? 'pa-badge-green'
                                                                    : 'pa-badge-gray'
                                                            }`}>
                                                            {clientLabel(row.clientType)}
                                                        </span>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td>
                                                    <span className="pa-status">
                                                        <span
                                                            className={`pa-dot ${
                                                                dataUploaded ? 'pa-dot-green' : 'pa-dot-red'
                                                            }`}
                                                        />
                                                        {dataUploaded ? 'Uploaded' : 'No data'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {analysis === 'Complete' && (
                                                        <span className="pa-badge pa-badge-green">Complete</span>
                                                    )}
                                                    {analysis === 'Pending' && (
                                                        <span className="pa-badge pa-badge-yellow">Pending</span>
                                                    )}
                                                    {analysis !== 'Complete' && analysis !== 'Pending' && (
                                                        <span className="pa-badge pa-badge-gray">—</span>
                                                    )}
                                                </td>
                                                <td className="text-nowrap">{formatDate(row.lastUpdated)}</td>
                                                <td>
                                                    <div className="pa-actions">
                                                        <button
                                                            type="button"
                                                            className="pa-btn-ghost pa-btn-primary-ghost"
                                                            onClick={() =>
                                                                go(row.id, row.hasSummaryData ? 'summary' : 'insights')
                                                            }>
                                                            View Dashboard →
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="pa-btn-ghost"
                                                            onClick={() => go(row.id, 'data')}>
                                                            Upload Data
                                                        </button>
                                                        <div className="pa-menu-wrap">
                                                            <button
                                                                type="button"
                                                                className="pa-btn-icon"
                                                                aria-label="More actions"
                                                                onClick={() =>
                                                                    setOpenMenuId((id) =>
                                                                        id === row.id ? null : row.id
                                                                    )
                                                                }>
                                                                ⋮
                                                            </button>
                                                            {openMenuId === row.id && (
                                                                <div className="pa-menu">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => go(row.id, 'ahli')}>
                                                                        AHLI Matrix
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => go(row.id, 'spm')}>
                                                                        SPM Matrix
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => go(row.id, 'focus')}>
                                                                        Actionables / Focus
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => go(row.id, 'summary')}>
                                                                        Summary
                                                                    </button>
                                                                    <button type="button" onClick={() => toggleModule(row)}>
                                                                        {moduleMap[row.id]
                                                                            ? 'Deactivate Placement Module'
                                                                            : 'Activate Placement Module'}
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {!loading && !rows.length && (
                                        <tr>
                                            <td colSpan={8} className="text-muted text-center py-4">
                                                No institutes found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlacementAnalyticsListing;
